import { Box, Paper, Stack } from '@mui/material';
import type { StatusesByField } from 'business/types';
import SearchBar from 'components/SharedComponents/SearchBar/SearchBar';
import { getDisplayedFieldStatuses } from 'helpers/fieldStatusHelper';
import { EXCLUDED_FUNCTIONS } from 'modules/datacatalog/constants';
import { type CatalogSelection } from 'modules/datacatalog/types';
import categories from 'modules/decision/pages/Explore/components/Sentence/components/functions_categories.json';
import {
    filterAndExpandFieldSearchResults,
    type TreeData,
} from 'modules/decision/pages/Explore/components/Sentence/helpers/treeData';
import { useGetDataInTreeFormat } from 'modules/decision/pages/Explore/components/Sentence/hooks/treeData';
import type { SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAPIGetIntegrationSoftwares } from 'shared/api/integrations/useAPIGetIntegrationSoftwares';
import HeaderLayout from 'ui/HeaderLayout/HeaderLayout';
import StyPageContainer from 'ui/PageContainer/PageContainer';

import { useAPIGetDataCatalogFields } from './api/useAPIGetDataCatalogFields';
import CatalogTree from './components/CatalogTree/CatalogTree';
import {
    findFieldInTree,
    getExpandedNodeIdsForSelection,
} from './components/CatalogTree/helpers/domain';
import DetailPanel from './components/DetailPanel/DetailPanel';

const DataCatalog = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const { data: dataCatalogFields, isLoading } = useAPIGetDataCatalogFields();

    const { data: allIntegrationSoftware } = useAPIGetIntegrationSoftwares();

    const displayedStatuses: StatusesByField | undefined = useMemo(
        () =>
            getDisplayedFieldStatuses(
                dataCatalogFields,
                allIntegrationSoftware,
            ),
        [dataCatalogFields, allIntegrationSoftware],
    );

    const functions = useMemo(
        () =>
            Array.from(
                new Set(
                    dataCatalogFields
                        ? dataCatalogFields.flatMap((field) => field.functions)
                        : [],
                ),
            )
                .filter((func) => !EXCLUDED_FUNCTIONS.includes(func))
                .sort((a, b) =>
                    t(`dataCatalog.${a}`).localeCompare(t(`dataCatalog.${b}`)),
                ),
        [dataCatalogFields, t],
    );

    const treeData = useGetDataInTreeFormat(
        dataCatalogFields || [],
        categories,
    );

    const selectedId = searchParams.get('field') ?? '';

    const [searchValue, setSearchValue] = useState('');
    const [selection, setSelection] = useState<CatalogSelection | null>(null);
    const [filteredTreeData, setFilteredTreeData] =
        useState<TreeData>(treeData);
    const [expanded, setExpanded] = useState<string[]>([]);

    useEffect(() => {
        if (searchValue) {
            filterAndExpandFieldSearchResults(
                treeData,
                setFilteredTreeData,
                setExpanded,
                {
                    keys: [
                        { name: 'value.label', weight: 3 },
                        { name: 'table', weight: 1 },
                        { name: 'value.description', weight: 0.1 },
                    ],
                    threshold: 0.1,
                },
            )(searchValue);
        } else {
            setFilteredTreeData(treeData);
            setExpanded([]);
        }
    }, [searchValue, treeData]);

    useEffect(() => {
        if (!isLoading && selectedId && selection?.name !== selectedId) {
            const found = findFieldInTree(selectedId, treeData);
            if (found) setSelection(found);
        }
    }, [isLoading, selectedId, treeData]);

    useEffect(() => {
        if (searchValue || !selectedId || selection?.name !== selectedId) {
            return;
        }

        const expandedNodeIds = getExpandedNodeIdsForSelection(
            treeData,
            selection,
        );
        if (expandedNodeIds.length === 0) {
            return;
        }

        setExpanded((currentExpanded) =>
            Array.from(new Set([...currentExpanded, ...expandedNodeIds])),
        );
    }, [searchValue, selectedId, selection, treeData]);

    const handleSelect = useCallback(
        (s: CatalogSelection) => {
            setSelection(s);
            setSearchParams({ field: s.name });
        },
        [setSearchParams],
    );

    const handleExpandedItemsChange = useCallback(
        (_: SyntheticEvent, nodeIds: string[]) => setExpanded(nodeIds),
        [],
    );

    return (
        <StyPageContainer>
            <HeaderLayout title={t('dataCatalog.dataCatalogTitle')} />

            <Stack
                direction="row"
                spacing={2}
                sx={{
                    m: 2,
                    p: 2,
                    height: 'calc(100vh - 110px)',
                    overflow: 'hidden',
                }}
            >
                <Paper
                    variant="outlined"
                    sx={{
                        width: '38%',
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        borderRadius: 2,
                        p: 1,
                    }}
                >
                    <SearchBar
                        placeholder={t('dataCatalog.searchField')}
                        onChange={(value) => setSearchValue(value)}
                        width="full-width"
                    />
                    <Box sx={{ overflow: 'auto', flex: 1 }}>
                        <CatalogTree
                            treeData={filteredTreeData}
                            isSearchUsed={!!searchValue}
                            searchQuery={searchValue}
                            selectedFunctions={functions}
                            selection={selection}
                            onSelect={handleSelect}
                            expanded={expanded}
                            setExpanded={handleExpandedItemsChange}
                            displayedStatuses={displayedStatuses}
                        />
                    </Box>
                </Paper>

                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <DetailPanel
                        selection={selection}
                        catalogMetadata={dataCatalogFields?.find(
                            (f) => f.field === selection?.name,
                        )}
                        isLoading={isLoading}
                        displayedStatuses={displayedStatuses}
                    />
                </Box>
            </Stack>
        </StyPageContainer>
    );
};

export default DataCatalog;
